<template>
    <div class="product-list-page">

        <!-- 1. 顶部：产品类目 (Category) -->
        <div class="sub-nav">
            <div class="sub-item" v-for="category in categoryList" :key="category.id"
                :class="{ active: activeCategoryId === category.id }" @click="changeCategory(category.id)">
                <!-- 优先使用接口返回的图片，如果没有则用本地图片或者占位 -->
                <img class="sub-icon" :src="category.icon" />

                <span>{{ $t(`product.nav.` + category.name) }}</span>
            </div>
        </div>

        <main class="main-container">
            <!-- 2. 顶部产品主视觉 (Hero) -->
            <!-- <section class="product-hero">
                <div class="banner">
                    <img src="@/assets/products/banner.png" loading="lazy" class="background-img" alt="Banner"
                        onerror="this.style.display='none'">
                    <div class="banner-placeholder"></div>
                </div>
            </section> -->

            <!-- 3. 主体左右分栏布局 -->
            <section class="layout-wrapper">
                <!-- 左侧：产品类型 (Type/Model) -->
                <aside class="sidebar">
                    <div class="category-group">

                        <!-- 骨架屏：包含标题和列表 -->
                        <template v-if="isLoadingTypes">
                            <!-- 新增：侧边栏标题的骨架 -->
                            <div class="skeleton-box"
                                style="width: 60%; height: 22px; margin-bottom: 12px; border-radius: 4px;"></div>

                            <!-- 侧边栏列表的骨架 -->
                            <ul class="sub-category-list">
                                <li v-for="i in 5" :key="i" style="pointer-events: none;">
                                    <div class="skeleton-box"
                                        style="width: 70%; height: 16px; margin: 4px 0; border-radius: 4px;"></div>
                                </li>
                            </ul>
                        </template>

                        <!-- 真实数据 -->
                        <template v-else>
                            <!-- 真实标题 -->
                            <h3 class="category-title" :class="{ 'active': !activeTypeId }" @click="changeType('')">
                                {{ $t(`product.nav.` + currentCategoryName) }}
                            </h3>

                            <!-- 真实列表 -->
                            <ul class="sub-category-list" v-if="typeList.length > 0">
                                <li :class="{ 'active': !activeTypeId }" @click="changeType('')">
                                    {{ $t('product.common.all') || 'All' }}
                                </li>
                                <li v-for="type in typeList" :key="type.id"
                                    :class="{ 'active': activeTypeId === type.id }" @click="changeType(type.id)">
                                    {{ $t(`product.sub.` + type.name) }}
                                </li>
                            </ul>
                            <div v-else class="empty-text">
                                {{ $t('product.common.emptyProduct') }}
                            </div>
                        </template>

                    </div>
                </aside>

                <!-- 右侧：产品展示与详细内容 -->
                <article class="content-area">
                    <!-- 分类标题与描述 -->
                    <div class="category-header">
                        <!-- 标题骨架屏 -->
                        <template v-if="isLoadingProducts || isLoadingTypes">
                            <div class="header-skeleton">
                                <div class="skeleton-box header-skeleton-title"></div>
                                <div class="skeleton-box header-skeleton-line"></div>
                                <div class="skeleton-box header-skeleton-line is-short"></div>
                            </div>
                        </template>
                        <!-- 真实标题 -->
                        <template v-else>
                            <h1>
                                {{ $t(`product.nav.` + currentCategoryName) }}
                                <span v-if="activeTypeId && currentTypeName"> - {{ $t(`product.sub.` + currentTypeName)
                                    }}</span>
                            </h1>
                            <p class="category-subtitle">
                                {{ $t(`product.desc.` + currentCategoryName) }}
                            </p>
                        </template>
                    </div>

                    <div class="product-grid" v-if="isLoadingProducts">
                        <div class="product-card" v-for="i in 8" :key="i">
                            <!-- 图片占位 -->
                            <div class="skeleton-box"
                                style="width: 80%; aspect-ratio: 4/3; margin-bottom: 25px; border-radius: 8px;">
                            </div>
                            <!-- 标题占位 -->
                            <div class="skeleton-box"
                                style="width: 60%; height: 22px; margin-bottom: 12px; border-radius: 4px;"></div>
                            <!-- 描述占位 -->
                            <div class="skeleton-box"
                                style="width: 80%; height: 16px; margin-bottom: 28px; border-radius: 4px;"></div>
                            <!-- 按钮占位 -->
                            <div class="card-actions">
                                <div class="skeleton-box" style="width: 80px; height: 36px; border-radius: 20px;"></div>
                                <div class="skeleton-box" style="width: 80px; height: 36px; border-radius: 20px;"></div>
                            </div>
                        </div>
                    </div>

                    <!-- 产品网格 (直接使用接口返回的产品列表) -->
                    <div class="product-grid" v-if="productList.length > 0">
                        <div class="product-card" v-for="product in productList" :key="product.id">
                            <div class="card-img" @click="toProductDetail(product.id, product.name)">
                                <!-- 替换为接口返回的产品主图 -->
                                <img style="height: 100%;width: 100%;object-fit: contain;" :src="product.icon.startsWith('http') ? product.icon :
                                    `https://qiniu-au.wifigsmalarm.com/images/` + product.icon + `.png`" />
                            </div>
                            <h4 class="card-title">{{ product.title || product.name }}</h4>
                            <!-- <p class="card-desc">{{ locale === 'zh' ? '￥' + (product.price * 6.8).toFixed(2) : 'US$' +
                                product.price.toFixed(2) }}</p> -->
                            <p class="card-desc"
                                v-html="product.description !== '' ? product.description : product.full_name"></p>
                            <div class="card-actions">
                                <button class="btn-outline" @click="toProductDetail(product.id, product.name)">{{
                                    $t('product.common.learnMore') }}</button>
                                <button class="btn-outline text-cyan" @click="addToCart(product, $event)">
                                    {{ $t('product.common.addToCart') }}
                                </button>
                            </div>
                        </div>
                    </div>

                    <!-- 缺省状态 -->
                    <div class="empty-state" v-else>
                        <p>{{ $t('product.common.emptyProduct') }}</p>
                    </div>
                </article>
            </section>
        </main>

        <!-- ================= 详情弹窗 ================= -->
        <transition name="fade">
            <div class="modal-overlay" v-if="isModalOpen" @click="closeModal">
                <!-- 加上 @click.stop 防止点击弹窗内部时触发了外层蒙层的关闭事件 -->
                <div class="modal-content" @click.stop>
                    <div class="modal-header">
                        <button class="close-btn" @click="closeModal">×</button>
                    </div>

                    <div class="modal-body">
                        <div class="modal-desc rich-text-container" v-html="currentProduct.description"></div>
                    </div>
                    <div class="modal-footer">
                        <button class="btn-outline text-cyan" @click="addToCartFromModal">
                            {{ $t('product.common.addToCart') }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
        <!-- ================= 购物车功能区 ================= -->
        <CartDrawer />
    </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { TypeList, ModelList, List } from '@/api/product'
import { useRouter } from 'vue-router'
import CartDrawer from '@/components/CartDrawer.vue'
import { addToCart as pushCart, flyToCart } from '@/utils/cart'

const router = useRouter()

// ================= 详情弹窗逻辑 =================
const isModalOpen = ref(false)
const currentProduct = ref({})

const isLoadingTypes = ref(true)
const isLoadingProducts = ref(true)
// 打开弹窗
// const openModal = (product) => {
//     currentProduct.value = product
//     isModalOpen.value = true
// }

// 关闭弹窗
const closeModal = () => {
    isModalOpen.value = false
    // 稍微延迟清空数据，防止关闭动画执行时看到内容突然消失
    setTimeout(() => {
        currentProduct.value = {}
    }, 300)
}

// 可选优化：在弹窗里点击“加入购物车”后，加入成功并顺便关掉弹窗
const addToCartFromModal = (event) => {
    addToCart(currentProduct.value, event)
    closeModal()
}

const addToCart = (product, event) => {
    pushCart({
        ...product,
        categoryName: currentCategoryName.value,
        typeName: currentTypeName.value
    })
    const rawIcon = product.icon || ''
    const icon = rawIcon.startsWith('http')
        ? rawIcon
        : (rawIcon ? `https://qiniu-au.wifigsmalarm.com/images/${rawIcon}.png` : '')
    flyToCart(event?.currentTarget, icon)
}


// ================= 数据结构与状态管理 =================
const categoryList = ref([]) // 接口返回的数据：顶级类目 (Top Nav)
const typeList = ref([])     // 接口返回的数据：产品类型 (Sidebar)
const productList = ref([])  // 接口返回的数据：产品列表 (Grid)

const activeCategoryId = ref('')
const activeTypeId = ref('')

onMounted(() => {
    // 页面加载完成立即请求顶级类目
    getCategoryData()
})

const toProductDetail = (id, name) => {
    router.push('/product/detail/' + name + '/' + id)
}

// 1. 获取顶级类目
const getCategoryData = async () => {
    try {
        const result = await TypeList()
        // 修复原代码 Bug：必须用 .value 赋值才能触发响应式更新
        categoryList.value = result || []
        // 如果有数据，默认选中第一项
        if (categoryList.value.length > 0) {
            changeCategory(categoryList.value[0].id)
        }
    } catch (error) {
        console.error('获取类目失败：', error)
    }
}

// 2. 根据选中的类目，获取对应的类型
const getTypeData = async (categoryId) => {
    isLoadingTypes.value = true // 请求开始前，开启类型骨架屏
    try {
        const params = { 'type_id': categoryId }
        const result = await ModelList(params)
        typeList.value = result || []

        activeTypeId.value = ''
        getProductData()
    } catch (error) {
        console.error('获取类型失败：', error)
    } finally {
        isLoadingTypes.value = false // 无论成功失败，关闭骨架屏
    }
}

// 3. 根基类目和类型，获取最终产品列表
const getProductData = async () => {
    isLoadingProducts.value = true // 请求开始前，开启产品骨架屏
    try {
        const params = {
            'type_id': activeCategoryId.value,
            'model_id': activeTypeId.value || 0
        }
        const result = await List(params)
        productList.value = result || []
    } catch (error) {
        console.error('获取产品失败：', error)
    } finally {
        isLoadingProducts.value = false // 请求结束，关闭骨架屏
    }
}

// ================= 交互动作 =================

// 切换顶级类目
const changeCategory = (id) => {
    if (activeCategoryId.value === id) return
    activeCategoryId.value = id
    // 类目变化，去拉取下一级的类型
    getTypeData(id)
}

// 切换产品类型
const changeType = (id) => {
    // 【修改点 3】：允许传入空字符串(即点击"全部")
    if (activeTypeId.value === id) return
    activeTypeId.value = id
    // 类型变化，去拉取产品
    getProductData()
}

// 计算当前选中的类目和类型名称（用于展示在页面标题和购物车中）
const currentCategoryName = computed(() => {
    const cat = categoryList.value.find(c => c.id === activeCategoryId.value)
    return cat ? cat.name : ''
})

const currentTypeName = computed(() => {
    const type = typeList.value.find(t => t.id === activeTypeId.value)
    return type ? type.name : ''
})

</script>

<style scoped>
/* =========================================
   全局与基础样式
========================================= */
.product-list-page {
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
    color: #333;
    position: relative;
}

.empty-text {
    color: #999;
    font-size: 0.9rem;
    padding-left: 15px;
    margin-top: 10px;
}

.lang-switch-bar {
    position: absolute;
    top: 15px;
    right: 40px;
    z-index: 10;
}

.btn-lang {
    background: #f0f0f0;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
}

.btn-lang:hover {
    background: #e0e0e0;
}

.placeholder-img,
.placeholder-icon {
    background-color: #eaeaea;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
    border-radius: 4px;
}

/* =========================================
   Header & SubNav 样式
========================================= */
.sub-nav {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;
    gap: 100px;
    padding: 100px 40px 40px 40px;
    border-bottom: 1px solid #f0f0f0;
    background: #fff;
}

.sub-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    cursor: pointer;
    font-size: 0.85rem;
    color: #666;
    transition: all 0.2s;
}

.sub-item:hover {
    color: #00bcd4;
}

.sub-item.active {
    font-weight: bold;
    color: #000;
}

.sub-icon {
    width: 35px;
    height: 35px;
    margin-bottom: 8px;
    object-fit: contain;
}

/* =========================================
   页面主体布局
========================================= */
.main-container {
    width: 100%;
}

.product-hero {
    height: 40vh;
    min-height: 300px;
}

.banner {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: #2a2a2a;
}

.background-img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.layout-wrapper {
    max-width: 1400px;
    margin: 0 auto;
    display: flex;
    padding: 60px 40px;
    gap: 60px;
}

/* =========================================
   左侧：侧边栏分类
========================================= */
.sidebar {
    width: 200px;
    flex-shrink: 0;
    position: sticky;
    top: 60px;
    height: fit-content;
}

.category-group {
    margin-bottom: 20px;
}

.category-title {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 12px;
    cursor: pointer;
    color: #1a1a1a;
    transition: color 0.2s;
}

.category-title:hover {
    color: #00bcd4;
}

.category-title.active {
    color: #00bcd4;
}

.sub-category-list {
    list-style: none;
    padding: 0;
    margin: 0 0 0 10px;
    border-left: 2px solid #eee;
}

.sub-category-list li {
    font-size: 0.9rem;
    color: #666;
    padding: 8px 0 8px 15px;
    cursor: pointer;
    transition: all 0.2s;
}

.sub-category-list li:hover {
    color: #000;
}

.sub-category-list li.active {
    color: #00bcd4;
    font-weight: 600;
}

/* =========================================
   右侧：内容区与产品网格
========================================= */
.content-area {
    flex: 1;
    min-width: 0;
}

.category-header {
    margin-bottom: 40px;
    border-bottom: 1px solid #eaeaea;
    padding-bottom: 20px;
    text-align: left;
}

.header-skeleton {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    width: min(560px, 100%);
}

.header-skeleton-title {
    width: 220px;
    max-width: 70%;
    height: 32px;
    border-radius: 8px;
}

.header-skeleton-line {
    width: 100%;
    height: 14px;
    border-radius: 7px;
}

.header-skeleton-line.is-short {
    width: 64%;
}

.category-header h1 {
    font-size: 2rem;
    margin: 0 0 10px 0;
    color: #111;
}

.category-header p {
    color: #666;
    font-size: 1rem;
    margin: 0;
}

.category-subtitle {
    color: #666;
    font-size: 1.05rem;
    line-height: 1.6;
    margin: 0;
    max-width: 1200px;
    /* 限制阅读宽度，防止文字拉得太长 */
}

/* 产品网格 */
.product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 30px;
    margin-bottom: 80px;
}

.product-card {
    background: #fff;
    border: 1px solid #f0f0f0;
    padding: 30px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    border-radius: 8px;
    transition: all 0.3s ease;
}

.product-card:hover {
    box-shadow: 0 15px 40px rgba(0, 0, 0, 0.08);
    transform: translateY(-5px);
}

.card-img {
    width: 80%;
    aspect-ratio: 4/3;
    margin-bottom: 25px;
    cursor: pointer;
}

.card-title {
    font-size: 1.2rem;
    font-weight: 700;
    margin: 0 0 10px 0;
}

.card-desc {
    font-size: 0.85rem;
    color: #888;
    line-height: 1.4;
    margin: 0 0 25px 0;
    height: 40px;
    overflow: hidden;
}

.card-actions {
    display: flex;
    gap: 10px;
    margin-top: auto;
    width: 100%;
    justify-content: center;
}

.btn-outline {
    padding: 8px 16px;
    border: 1px solid #d9d9d9;
    background: transparent;
    border-radius: 20px;
    font-size: 0.85rem;
    cursor: pointer;
    transition: all 0.2s;
    color: #333;
}

.btn-outline:hover {
    border-color: #999;
}

.btn-outline.text-cyan {
    color: #00bcd4;
    border-color: #00bcd4;
}

.btn-outline.text-cyan:hover {
    background: #00bcd4;
    color: #fff;
}

.empty-state {
    padding: 60px 20px;
    text-align: center;
    color: #999;
    font-size: 1.1rem;
    background: #f9f9f9;
    border-radius: 8px;
    margin-bottom: 80px;
}

.bottom-info-section {
    border-top: 1px solid #eaeaea;
    padding-top: 40px;
}

/* 响应式调整 */
@media (max-width: 768px) {
    .layout-wrapper {
        flex-direction: column;
        padding: 20px;
    }

    .sidebar {
        width: 100%;
        position: static;
        margin-bottom: 30px;
    }

}

/* =========================================
   产品详情弹窗 (Modal) 美化版样式
========================================= */
.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
}

.modal-content {
    background: #fff;
    width: 90%;
    max-width: 550px;
    /* 稍微放宽一点展示图文 */
    border-radius: 20px;
    /* 圆角变大，更现代 */
    padding: 30px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
    /* 更柔和的高级阴影，去掉了丑陋的实体边框 */
    display: flex;
    flex-direction: column;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.modal-header h3 {
    margin: 0;
    font-size: 1.5rem;
    color: #111;
    font-weight: 700;
}

/* 圆形灰色按钮，悬浮变深 */
.modal-header .close-btn {
    background: #f3f4f6;
    border: none;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    font-size: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: #6b7280;
    transition: all 0.2s;
}

.modal-header .close-btn:hover {
    background: #e5e7eb;
    color: #111;
}

.modal-body {
    flex: 1;
    max-height: 60vh;
    overflow-y: auto;
    padding-right: 5px;
}

/* 滚动条美化 */
.modal-body::-webkit-scrollbar {
    width: 6px;
}

.modal-body::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 10px;
}

/* 弹窗图片容器 */
.modal-cover {
    width: 100%;
    height: 240px;
    background: #f9fafb;
    border-radius: 12px;
    margin-bottom: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    box-sizing: border-box;
}

.modal-cover img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

/* 醒目的价格 */
.modal-price {
    font-size: 1.75rem;
    color: #ff4d4f;
    font-weight: 800;
    margin-bottom: 15px;
}

.modal-desc {
    font-size: 1rem;
    color: #4b5563;
    line-height: 1.7;
    margin: 0;
    white-space: pre-wrap;
    word-wrap: break-word;
    word-break: break-word;
    text-align: left;
}

.modal-footer {
    margin-top: 30px;
    display: flex;
    justify-content: flex-end;
    gap: 15px;
}

/* 按钮美化 */
.btn-cancel,
.btn-primary {
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 1rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
}

.btn-cancel {
    background: transparent;
    color: #6b7280;
    border: 1px solid #d1d5db;
}

.btn-cancel:hover {
    background: #f3f4f6;
    color: #111;
}

.btn-primary {
    background: #00bcd4;
    color: #fff;
}

.btn-primary:hover {
    background: #00acc1;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 188, 212, 0.3);
}

/* 弹窗渐显动画 */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.fade-enter-active .modal-content,
.fade-leave-active .modal-content {
    transition: transform 0.3s ease cubic-bezier(0.16, 1, 0.3, 1);
}

.fade-enter-from .modal-content,
.fade-leave-to .modal-content {
    transform: translateY(20px) scale(0.96);
}

@keyframes skeleton-loading {
    0% {
        background-position: 100% 0;
    }

    100% {
        background-position: 0 0;
    }
}

.skeleton-box {
    display: block;
    position: relative;
    overflow: hidden;
    background: linear-gradient(90deg, #f3f5f8 25%, #e4eaf0 37%, #f3f5f8 63%);
    background-size: 400% 100%;
    animation: skeleton-loading 1.4s ease infinite;
}
</style>